import React, { useEffect, useRef } from 'react';

type PlayheadControllerProps = {
    videoRef: React.RefObject<HTMLVideoElement | null>;
    /**
     * 总时长（秒）。若同时传入 `totalTimeRef`，则每帧以 `totalTimeRef.current` 为准（非受控，避免总时长变化导致 effect 重绑）。
     */
    totalTime: number;
    /** 每帧读取总时长；与 `totalTime` 同时存在时优先使用此 ref */
    totalTimeRef?: React.MutableRefObject<number>;
    tracksContainerSelector?: string; // 轨道容器，用于计算宽度
    playheadSelector?: string; // 播放头元素
    timelineContentSelector?: string; // 时间轴内容容器，用于滚动
    /** 为 true 时暂停由视频时间驱动的播放头更新（用户正在拖拽/ scrub 时间轴，避免与 DOM 操作打架抖动） */
    scrubbingRef?: React.MutableRefObject<boolean>;
};

// 播放头完全命令式：每帧读 HTMLVideoElement.currentTime 写 DOM transform，不经 React props / Zustand 时钟（避免 Fiber 调度与节流 store 拖慢刻度线）。
// 优先 requestVideoFrameCallback，降级 requestAnimationFrame；时间轴横向 scroll 用 behavior:auto。
const PlayheadController: React.FC<PlayheadControllerProps> = ({
    videoRef,
    totalTime,
    totalTimeRef,
    tracksContainerSelector = '.tracks-container',
    playheadSelector = '#playhead',
    timelineContentSelector = '.timeline-content',
    scrubbingRef,
}) => {
    const rafIdRef = useRef<number | null>(null);
    const rvfcIdRef = useRef<number | null>(null);
    const isScrollingRef = useRef<boolean>(false); // 标记是否正在滚动动画中

    useEffect(() => {
        const video = videoRef.current;
        const playhead = document.querySelector(playheadSelector) as HTMLElement | null;
        const container = document.querySelector(tracksContainerSelector) as HTMLElement | null;
        const timelineContent = document.querySelector(timelineContentSelector) as HTMLElement | null;
        // 总时长可能在首帧仍为 0，仅跳过 DOM 未就绪；具体数值在 update() 内每帧读取 totalTimeRef
        if (!video || !playhead || !container || !timelineContent) return;

        // 监听滚动结束事件，用于检测滚动动画完成
        let scrollTimeout: NodeJS.Timeout | null = null;
        const handleScroll = () => {
            if (scrollTimeout) {
                clearTimeout(scrollTimeout);
            }
            isScrollingRef.current = true;
            scrollTimeout = setTimeout(() => {
                isScrollingRef.current = false;
            }, 300); // 滚动动画通常持续约 300ms
        };
        timelineContent.addEventListener('scroll', handleScroll);

        const shouldStopTracking = () =>
            video.paused || video.ended;

        const update = () => {
            if (scrubbingRef?.current) {
                return;
            }
            if (shouldStopTracking()) {
                return;
            }
            const totalSec = totalTimeRef ? totalTimeRef.current : totalTime;
            if (!totalSec || totalSec <= 0) return;

            const viewportWidth = timelineContent.clientWidth;
            const totalWidth = container.scrollWidth || container.clientWidth;
            if (viewportWidth <= 0 || totalWidth <= 0) return;

            const videoTime = video.currentTime || 0;
            // HLS 片尾偶发 currentTime 回绕到 0 且仍标记为播放中，避免播放头向起点倒翻
            if (videoTime >= totalSec - 0.05) {
                const playheadAbsoluteX = (Math.min(videoTime, totalSec) / totalSec) * totalWidth;
                playhead.style.transform = `translateX(${playheadAbsoluteX}px)`;
                return;
            }
            
            const playheadAbsoluteX = (videoTime / totalSec) * totalWidth;

            // 2. 计算播放头相对于可视区域的X坐标
            // 播放头在轨道容器中的绝对位置 - 时间轴的滚动偏移 = 播放头在可视区域中的位置
            const playheadRelativeX = playheadAbsoluteX - timelineContent.scrollLeft;

            // 4. 步进式滚动：每次推进约 80% 视口宽，减少 scroll 次数；behavior: auto
            const stepPx = viewportWidth * 0.8;
            if (!isScrollingRef.current && playheadRelativeX >= viewportWidth) {
                const currentScrollLeft = timelineContent.scrollLeft;
                const newScrollLeft = currentScrollLeft + stepPx;
                const maxScrollLeft = Math.max(0, (timelineContent.scrollWidth || totalWidth) - viewportWidth);
                const finalScrollLeft = Math.min(newScrollLeft, maxScrollLeft);

                if (finalScrollLeft !== currentScrollLeft) {
                    timelineContent.scrollTo({
                        left: finalScrollLeft,
                        behavior: 'auto',
                    });
                    isScrollingRef.current = true;
                }
            } else if (!isScrollingRef.current && playheadRelativeX < 0) {
                const currentScrollLeft = timelineContent.scrollLeft;
                const newScrollLeft = Math.max(0, currentScrollLeft - stepPx);

                if (newScrollLeft !== currentScrollLeft) {
                    timelineContent.scrollTo({
                        left: newScrollLeft,
                        behavior: 'auto',
                    });
                    isScrollingRef.current = true;
                }
            }

            // 5. 每帧同步播放头（子像素交给浏览器，避免 toFixed 造成台阶感）
            playhead.style.transform = `translateX(${playheadAbsoluteX}px)`;
        };

        const loopRAF = () => {
            if (shouldStopTracking()) {
                rafIdRef.current = null;
                return;
            }
            update();
            rafIdRef.current = window.requestAnimationFrame(loopRAF);
        };

        const start = () => {
            // 优先使用 requestVideoFrameCallback（更贴近媒体帧）
            const anyVideo = video as any;
            if (typeof anyVideo.requestVideoFrameCallback === 'function') {
                const cb = (_now: number, _metadata: any) => {
                    if (shouldStopTracking()) {
                        rvfcIdRef.current = null;
                        return;
                    }
                    update();
                    rvfcIdRef.current = anyVideo.requestVideoFrameCallback(cb);
                };
                rvfcIdRef.current = anyVideo.requestVideoFrameCallback(cb);
                return;
            }
            // 降级到 rAF
            rafIdRef.current = window.requestAnimationFrame(loopRAF);
        };

        const stop = () => {
            if (rvfcIdRef.current != null) {
                const anyVideo2 = video as any;
                if (typeof anyVideo2.cancelVideoFrameCallback === 'function') {
                    anyVideo2.cancelVideoFrameCallback(rvfcIdRef.current);
                }
                rvfcIdRef.current = null;
            }
            if (rafIdRef.current != null) {
                cancelAnimationFrame(rafIdRef.current);
                rafIdRef.current = null;
            }
        };

        const onPlay = () => start();
        const onPause = () => stop();
        const onEnded = () => stop();

        if (!video.paused) start();
        video.addEventListener('play', onPlay);
        video.addEventListener('pause', onPause);
        video.addEventListener('ended', onEnded);

        return () => {
            stop();
            video.removeEventListener('play', onPlay);
            video.removeEventListener('pause', onPause);
            video.removeEventListener('ended', onEnded);
            timelineContent.removeEventListener('scroll', handleScroll);
            if (scrollTimeout) {
                clearTimeout(scrollTimeout);
            }
        };
    }, [
        videoRef,
        // totalTime 仅作无 ref 时的回退；有 totalTimeRef 时不随数值变化重绑监听
        totalTime,
        totalTimeRef,
        tracksContainerSelector,
        playheadSelector,
        timelineContentSelector,
        scrubbingRef,
    ]);

    return null;
};

export default PlayheadController;


